import { useState, useEffect, useCallback, useRef } from 'react';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { normalizeApiBaseUrl } from '@/lib/utils';
import { useSnackbar } from '../providers/SnackbarProvider';

const API_BASE_URL = normalizeApiBaseUrl(getRuntimeConfig().apiBaseUrl || process.env.REACT_APP_API_BASE_URL || '');

interface OperationAlert {
  level: 'info' | 'error';
  message: string;
  ts: string;
}

interface OperationProgress {
  percent: number;
  stage: string;
}

export interface OperationState {
  operationId: string;
  type: string;
  entityType: string;
  entityId: number;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: OperationProgress;
  alerts: OperationAlert[];
  result: any;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

const POLL_INTERVAL = 3000;

/**
 * Hook for tracking an async backend operation via SSE (with polling fallback).
 *
 * Usage:
 *   const { state, isActive } = useOperation(operationId);
 *
 * Alerts in the operation are automatically shown as snackbar toasts.
 */
export function useOperation(operationId: string | null) {
  const [state, setState] = useState<OperationState | null>(null);
  const alertIndexRef = useRef(0);
  const { showSnackbar } = useSnackbar();
  const cleanupRef = useRef<(() => void) | null>(null);

  const showNewAlerts = useCallback(
    (alerts: OperationAlert[]) => {
      const newAlerts = alerts.slice(alertIndexRef.current);
      for (const alert of newAlerts) {
        showSnackbar(alert.message, alert.level === 'error' ? 'error' : 'info', false);
      }
      alertIndexRef.current = alerts.length;
    },
    [showSnackbar]
  );

  const handleStateUpdate = useCallback(
    (data: OperationState) => {
      setState(data);
      if (data.alerts?.length) {
        showNewAlerts(data.alerts);
      }
    },
    [showNewAlerts]
  );

  // Polling fallback
  const startPolling = useCallback(
    (opId: string) => {
      let cancelled = false;
      const poll = async () => {
        if (cancelled) return;
        try {
          const res = await fetch(`${API_BASE_URL}/api/operations/${opId}/status`);
          if (!res.ok) return;
          const data = await res.json();
          handleStateUpdate(data);
          if (data.status !== 'completed' && data.status !== 'failed') {
            setTimeout(poll, POLL_INTERVAL);
          }
        } catch {
          if (!cancelled) setTimeout(poll, POLL_INTERVAL * 2);
        }
      };
      poll();
      return () => {
        cancelled = true;
      };
    },
    [handleStateUpdate]
  );

  useEffect(() => {
    if (!operationId) {
      setState(null);
      alertIndexRef.current = 0;
      return;
    }

    // Try SSE first
    const url = `${API_BASE_URL}/api/operations/${operationId}/stream`;
    let eventSource: EventSource | null = null;
    let fallbackCleanup: (() => void) | null = null;

    try {
      eventSource = new EventSource(url);

      eventSource.addEventListener('status', (e: MessageEvent) => {
        try {
          handleStateUpdate(JSON.parse(e.data));
        } catch {
          // ignore parse errors
        }
      });

      eventSource.addEventListener('done', () => {
        eventSource?.close();
      });

      eventSource.addEventListener('timeout', () => {
        eventSource?.close();
      });

      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
        // Fall back to polling
        fallbackCleanup = startPolling(operationId);
      };
    } catch {
      // EventSource constructor failed — fall back to polling
      fallbackCleanup = startPolling(operationId);
    }

    cleanupRef.current = () => {
      eventSource?.close();
      fallbackCleanup?.();
    };

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [operationId, handleStateUpdate, startPolling]);

  const isActive = state != null && state.status !== 'completed' && state.status !== 'failed';
  const isComplete = state?.status === 'completed';
  const isFailed = state?.status === 'failed';

  return { state, isActive, isComplete, isFailed };
}

/**
 * Hook to check for active operations on an entity (e.g. on page mount).
 */
export function useActiveOperations(entityType: string, entityId: number | null) {
  const [operations, setOperations] = useState<OperationState[]>([]);

  useEffect(() => {
    if (!entityId) return;

    const check = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/operations/active?entityType=${entityType}&entityId=${entityId}`);
        if (res.ok) {
          setOperations(await res.json());
        }
      } catch {
        // ignore
      }
    };
    check();
  }, [entityType, entityId]);

  return operations;
}
